import { useState } from 'react';
import { BaseModal } from '@/components/shared';
import type { TipoSolicitacao } from '../../../models';
import { GRUPO_TIPO_SOLICITACAO_COMPATIVEL } from '../../../models';
import { TIPO_SOLICITACAO_LABELS } from '../../NovaSolicitacaoPage/novaSolicitacaoPage.definitions';

interface EditarTipoSolicitacaoModalProps {
  isOpen: boolean;
  onClose: () => void;
  tipoAtual: TipoSolicitacao;
  onConfirmar: (novoTipo: TipoSolicitacao) => void;
  confirmando?: boolean;
}

/**
 * Troca de Tipo de Solicitação — restrita ao grupo estruturalmente compatível (ver
 * GRUPO_TIPO_SOLICITACAO_COMPATIVEL), pra não deixar Acessos/Movimentação/colaboradorId
 * inconsistentes. Só é aberto quando `tipoAtual` já está nesse grupo (ver
 * SolicitacaoDetailsContent — pra Admissão/Desligamento/Formatação/Acessos não existe
 * nenhuma troca seguro, então o lápis nem aparece pra esses).
 *
 * Opções fora do grupo continuam visíveis (pra não sumir sem explicação), mas
 * "desativadas": clicar nelas não seleciona nada, só mostra o aviso de incompatibilidade —
 * feito assim (em vez de `disabled` nativo) porque um `<option>`/botão disabled não
 * dispara clique na maioria dos navegadores, e sem clique não dá pra explicar o motivo.
 */
export default function EditarTipoSolicitacaoModal({
  isOpen,
  onClose,
  tipoAtual,
  onConfirmar,
  confirmando,
}: EditarTipoSolicitacaoModalProps) {
  const [novoTipo, setNovoTipo] = useState<TipoSolicitacao | null>(null);
  const [avisoIncompatibilidade, setAvisoIncompatibilidade] = useState<string | null>(null);

  const handleClose = () => {
    setNovoTipo(null);
    setAvisoIncompatibilidade(null);
    onClose();
  };

  const handleClicarOpcao = (tipo: TipoSolicitacao) => {
    if (!GRUPO_TIPO_SOLICITACAO_COMPATIVEL.includes(tipo)) {
      setAvisoIncompatibilidade(
        `"${TIPO_SOLICITACAO_LABELS[tipo]}" tem requisitos diferentes de "${TIPO_SOLICITACAO_LABELS[tipoAtual]}" (Acessos, Movimentação ou colaborador) — opção inativa por incompatibilidade.`
      );
      return;
    }
    setAvisoIncompatibilidade(null);
    setNovoTipo(tipo);
  };

  const handleConfirmar = () => {
    if (!novoTipo || novoTipo === tipoAtual) return;
    onConfirmar(novoTipo);
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Alterar Tipo de Solicitação"
      maxWidth="max-w-md"
      fullHeight={false}
      actions={
        <div className="flex justify-end gap-3">
          <button type="button" className="btn-secondary" onClick={handleClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!novoTipo || novoTipo === tipoAtual || confirmando}
            onClick={handleConfirmar}
          >
            {confirmando ? 'Salvando...' : 'Confirmar Troca'}
          </button>
        </div>
      }
    >
      <div className="space-y-3 py-4">
        <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          Mudar o Tipo de Solicitação pode afetar Acessos, Movimentação e outras regras que
          dependem dele — por isso só é possível trocar entre tipos com os mesmos requisitos.
        </p>

        <div className="space-y-1">
          {Object.entries(TIPO_SOLICITACAO_LABELS).map(([valor, label]) => {
            const tipo = valor as TipoSolicitacao;
            const ehAtual = tipo === tipoAtual;
            const compativel = GRUPO_TIPO_SOLICITACAO_COMPATIVEL.includes(tipo);
            const selecionado = tipo === novoTipo;

            return (
              <button
                key={tipo}
                type="button"
                onClick={() => handleClicarOpcao(tipo)}
                disabled={ehAtual}
                className={`w-full rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                  ehAtual
                    ? 'cursor-default border-gray-200 bg-gray-100 text-gray-400'
                    : selecionado
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : compativel
                        ? 'border-gray-200 text-gray-900 hover:bg-gray-50'
                        : 'cursor-not-allowed border-gray-100 text-gray-300'
                }`}
              >
                {label}
                {ehAtual && <span className="ml-2 text-xs">(atual)</span>}
              </button>
            );
          })}
        </div>

        {avisoIncompatibilidade && <p className="text-xs text-red-600">{avisoIncompatibilidade}</p>}
      </div>
    </BaseModal>
  );
}
